import { type ReactNode } from 'react'
import { CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { BulkActionsSlot, ColumnsSlot, TableActionsSlot } from '@/components/ListCard'

interface ListCardHeaderProps {
  /** Icon and name of the list. */
  title: ReactNode
  /** Row count, shown as a pill next to the name. Hidden when zero. */
  count?: number
  /** Second line under the name. */
  subtitle?: ReactNode
  /** Filters and view controls, on the same row as the name. */
  controls?: ReactNode
  /** Further controls, placed after the column picker rather than before it. */
  trailingControls?: ReactNode
  /** Actions pinned to the right of the row, such as a "new" button. */
  actions?: ReactNode
  /** Extra classes for the header row, e.g. `border-b` to rule it off. */
  className?: string
}

/**
 * Header row for a list card: the name and its row count on the left, with the
 * list's filters and view controls beside it and any actions to the right.
 */
export function ListCardHeader({
  title,
  count,
  subtitle,
  controls,
  trailingControls,
  actions,
  className,
}: ListCardHeaderProps) {
  return (
    <CardHeader className={cn('flex flex-row flex-wrap items-center gap-3', className)}>
      <BulkActionsSlot />
      {/* The icon is lifted out of the title row and centred against the name
          and subtitle together, so both lines start at the same edge. The
          padding is the icon tile (size-8) plus the title's gap-3. */}
      <div className="relative flex min-h-8 flex-col justify-center has-data-[slot=card-icon]:pl-11 [&_[data-slot=card-icon]]:absolute [&_[data-slot=card-icon]]:top-1/2 [&_[data-slot=card-icon]]:left-0 [&_[data-slot=card-icon]]:-translate-y-1/2">
        <CardTitle>
          {title}
          {!!count && (
            <span className="rounded-full bg-(--ad-neutral-soft) px-2 py-px text-xs font-semibold tracking-wide text-(--ad-text-muted) uppercase">
              {count}
            </span>
          )}
        </CardTitle>
        {subtitle && <div className="mt-0.5 text-xs text-(--ad-text-muted)">{subtitle}</div>}
      </div>
      {controls && <div className="flex items-center gap-2">{controls}</div>}
      <ColumnsSlot />
      {trailingControls && <div className="flex items-center gap-2">{trailingControls}</div>}
      <div className="ml-auto flex items-center gap-2">
        {actions}
        <TableActionsSlot />
      </div>
    </CardHeader>
  )
}
